import { Button } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * The phrase a series prepared for Claude hands over (npc.md §8, H16; maquette `Lancement`, état c),
 * with its « Copier »: the text the operator pastes into Claude Code or claude.ai with the
 * connector of the dev. The same block stands in the launch box when the series is born and on the
 * page of the series, for whoever opens it later.
 *
 * The phrase is the server's (`claudePrompt`) and is shown as it is, selectable: a clipboard that
 * refuses (a plain-HTTP stack) must leave the text one `Ctrl+C` away, so a failed copy SAYS it
 * rather than claiming « Copié ».
 */

export interface ClaudePromptPanelProps {
  prompt: string;
  /** Prefix of the `data-testid` of the block and of its button. */
  testId: string;
}

type CopyState = 'idle' | 'copied' | 'failed';

export function ClaudePromptPanel({ prompt, testId }: ClaudePromptPanelProps) {
  const [copy, setCopy] = useState<CopyState>('idle');

  async function copyPrompt(): Promise<void> {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
    setTimeout(() => setCopy('idle'), 2000);
  }

  return (
    <div className="space-y-2">
      <pre
        data-testid={`${testId}-phrase`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the block scrolls, and a keyboard must reach it.
        tabIndex={0}
        className="max-h-48 select-all overflow-auto whitespace-pre-wrap break-words rounded-md border-2 border-gray-200 bg-gray-50 px-3 py-2.5 font-mono text-gray-800 text-small leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        {prompt}
      </pre>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          compact
          variant="accent"
          data-testid={`${testId}-copier`}
          data-state={copy}
          onClick={() => void copyPrompt()}
        >
          {copy === 'copied'
            ? 'Copié'
            : copy === 'failed'
              ? 'Copie impossible'
              : 'Copier la phrase'}
        </Button>
        <span aria-live="polite" className="text-caption text-gray-600">
          {copy === 'failed'
            ? 'Le presse-papiers est indisponible : la phrase est à sélectionner.'
            : null}
        </span>
      </div>
    </div>
  );
}
